import { tWithKeyAttr as t } from "../../i18nHelper";
import styles from "../forms/forms.module.css";
import CloseButton from "../closeButton/closeButton";
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAppSelector } from "../../services/hooks";
import { adminSelector } from "../../services/selectors/user.selectors";
import AddExerciseToUser from "../addExerciseToUser/addExerciseToUser";
import {
  useGetAppointmentsProgramsByUserIdMutation,
  useGetExercisesMutation,
  usePostAppointmentsProgramsMutation,
  useUpdateTherapistTakingTreatmentPlanMutation,
  useUpdateUnavailableTherapistTakingTreatmentPlanMutation,
  useGetProtocolsByCompanyQuery,
  usePostTreatmentPlanAutoAssignMutation,
  useLazyGetGroupTreatmentPlansQuery,
  useGetCompanyServicesQuery,
} from "../../services/api/utils.api";
import {
  appointmentsProgramsDataSelector,
  appointmentsProgramsTherapistSelector,
  appointmentsProgramsUnavailableTherapistsSelector,
  exercisesSelector,
} from "../../services/selectors/utils.selectors";
import addOrEditQuestionnaireStyles from "../forms/addOrEditQuestionnaire/addOrEditQuestionnaire.module.css";
import Select, { ActionMeta, SingleValue, MultiValue } from "react-select";
import { initialAppointmentsProgram } from "../../utils/constants";
import formStyles from "../forms/forms.module.css";
import {
  IAppointmentsProgram,
  IAppointmentsProgramsDataWithLabels,
  TAppointmentsProgramsData,
  AddLabelAndValue,
  TNamedItem,
} from "../../services/types/utils.types";
import useAutosizeTextArea from "../../hooks/useAutosizeTextarea";
import useMediaQuery from "../../hooks/useMediaQuery";
import useTherapistOptions from "../../hooks/useTherapistOptions";
import useTreatmentPlanFormValidation from "../../hooks/useTreatmentPlanFormValidation";
import Loader from "../loader/loader";
import Modal from "../modals/modal/Modal";
import GeneralSuccessPopup from "../modals/generalSuccessPopup/generalSuccessPopup";
import ErrorPopup from "../modals/errorPopup/errorPopup";
import CompanyProgramsPopup from "../modals/companyPrograms/companyProgramsPopup";
import { IUserReport } from "../../services/types/users.types";
import { getPrintDate } from "../../utils/utils";
import { useAddExercisesToUserMutation } from "../../services/api/user.api";

function AddTreatmentPlan({
  onClose,
  user,
  program,
  submitButtonText, //for popup use (when we open from treatmentDocument home.page)
  treatmentGroupId,
}: {
  onClose: () => void;
  user: IUserReport;
  program: TAppointmentsProgramsData | undefined;
  submitButtonText?: string;
  treatmentGroupId?: number;
}) {
  type ExerciseOption = { label: string; value: number };
  const [form, setForm] = useState(initialAppointmentsProgram);
  const [selectedExercises, setSelectedExercises] = useState<MultiValue<ExerciseOption>>([]);
  const [showExercises, setShowExercises] = useState(false);
  const [showCompaniesProgramsModal, setShowCompaniesProgramsModal] = useState(false);
  const [openModalSuccess, setOpenModalSuccess] = useState(false);
  const [openModalError, setOpenModalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showUnavailableTherapists, setShowUnavailableTherapists] = useState(false);
  const [autoAssignTherapist, setAutoAssignTherapist] = useState(false);

  const mobile = useMediaQuery("(max-width: 900px)");
  const admin = useAppSelector(adminSelector);
  const appointmentsProgramsTherapist = useAppSelector(
    appointmentsProgramsTherapistSelector
  );
  const appointmentsProgramsUnavailableTherapists = useAppSelector(
    appointmentsProgramsUnavailableTherapistsSelector
  );
  const appointmentsProgramsData = useAppSelector(
    appointmentsProgramsDataSelector
  );
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  // Clears the selected therapist on form change
  const handleResetTherapistGuid = useCallback(() => {
    setForm((prev) => ({ ...prev, therapistsGuid: null, therapistsId: undefined }));
  }, []);

  const { enabled: getTherapistsOptions, setEnabled: setGetTherapistsOptions, showCheckboxes, isGettingTherapists } = useTherapistOptions({
    form,
    user,
    adminCompanyId: admin.companyId,
    adminGuid: admin.guid,
    showUnavailable: showUnavailableTherapists,
    onResetTherapistGuid: handleResetTherapistGuid,
  });

  const { isValid: isFormValid } = useTreatmentPlanFormValidation(form);

  const [postAppointmentsPrograms, { isLoading: isSending }] =
    usePostAppointmentsProgramsMutation();
  const [updateTherapistTakingTreatmentPlan] =
    useUpdateTherapistTakingTreatmentPlanMutation();
  const [updateUnavailableTherapistTakingTreatmentPlan] =
    useUpdateUnavailableTherapistTakingTreatmentPlanMutation();
  const [postTreatmentPlanAutoAssign, { isLoading: isAutoAssigning }] =
    usePostTreatmentPlanAutoAssignMutation();
  const [getAppointmentsPrograms] =
    useGetAppointmentsProgramsByUserIdMutation();
  const { data: protocolsData } = useGetProtocolsByCompanyQuery(
    { companyId: user?.Users?.CompanyId || admin.companyId },
    { skip: !(user?.Users?.CompanyId || admin.companyId) }
  );
  const [getExercises] = useGetExercisesMutation();
  const [addExercises, { data: userExercisesData, isLoading: isUserExercisesLoading }] = useAddExercisesToUserMutation();
  const [GetGroupTreatmentPlans] = useLazyGetGroupTreatmentPlansQuery();

  const treatmentRooms = appointmentsProgramsData.TreatmentRoomsList || [];

  const exercises = useAppSelector(exercisesSelector).map((e) => ({
    ...e,
    label: e.title,
    value: e.id,
  })); //add exercises options in slice in future
  const createDate = program?.appointmentsProgram?.createDate;
  const isValidForChanges = form.statusId === 1 || form.statusId === 4 || !form.isApproved;
  const isClinicSelected = form.mediasId === 4;

  useAutosizeTextArea(textAreaRef.current, form.comments || "");

  // Fetch all exercises
  useEffect(() => {
    if (admin) {
      getExercises();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch user exercises 
  useEffect(() => {
    const userGuid = user?.Users?.Guid;
    if (userGuid && userGuid !== "0" && userGuid.trim() !== "") {
      addExercises({
        type: 1,
        usersGuid: userGuid,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.Users?.Guid]);

  // Update selected exercises when user exercises data is loaded
  useEffect(() => {
    if (userExercisesData) {
      const formattedExercises = userExercisesData.map((exercise) => ({
        value: exercise.id,
        label: exercise.title,
      }));
      setSelectedExercises(formattedExercises);
    }
  }, [userExercisesData]);

  // Initialize form with program data or user defaults
  useEffect(() => {
    if (program?.appointmentsProgram) {
      setForm((prev) => ({
        ...prev,
        ...program?.appointmentsProgram,
        // Only use the protocol from the treatment plan itself, not from user
        protocolId: program.appointmentsProgram.protocolId ?? null,
        // Preserve therapistsGuid from program if it exists
        therapistsGuid: program.therapistsGuid ?? program.appointmentsProgram.therapistsGuid ?? prev.therapistsGuid,
      }));
    } else {
      const userDefaultProtocol = user?.Users?.QuestionnaireCompaniesHeadersId ?? user?.Users?.ProtocolId ?? null;
      if (userDefaultProtocol) {
        setForm((prev) => ({
          ...prev,
          protocolId: userDefaultProtocol,
        }));
      }
    }
  }, [program, user]);

  // Sync therapistsId when therapist options are loaded and therapistsGuid exists
  useEffect(() => {
    if (form.therapistsGuid && !form.therapistsId && getTherapistsOptions && !isGettingTherapists) {
      const therapistList = showUnavailableTherapists
        ? appointmentsProgramsUnavailableTherapists
        : appointmentsProgramsTherapist;
      
      if (therapistList && therapistList.length > 0) {
        const found = therapistList.find((o) => o.therapistsGuid === form.therapistsGuid);
        if (found) {
          setForm((prev) => ({
            ...prev,
            therapistsId: found.value,
          }));
        }
      }
    }
  }, [
    form.therapistsGuid,
    form.therapistsId,
    getTherapistsOptions,
    isGettingTherapists,
    showUnavailableTherapists,
    appointmentsProgramsTherapist,
    appointmentsProgramsUnavailableTherapists,
  ]);
  // useEffect(() => {
  //   if (!isClinicSelected) {
  //     console.log(22)
  //     setForm((prev) => ({ ...prev, treatmentRoomId: null }));
  //   }
  // }, [isClinicSelected]);
// console.log(form);
  const sendForm = () => {
    const {
      usersId,
      therapistsId,
      createUsersGuid,
      createUsersId,
      updateUsersId,
      ...filteredAppointmentsProgram
    } = form;

    const payload = {
      ...filteredAppointmentsProgram,
      companyId: admin.companyId,
      usersGuid: treatmentGroupId ? "0" : (user.Users.Guid ?? "0"),
      isApproved: true,
      therapistsGuid: form.therapistsGuid || program?.therapistsGuid, // if no new guid set in form, use the one from the program
      therapistsSpecializationsIds: 
      filteredAppointmentsProgram.therapistsSpecializationsIds?.length === 0
        ? null
        : filteredAppointmentsProgram.therapistsSpecializationsIds,
      ...(treatmentGroupId && { treatmentGroupId }), 
    };
    

    let apiCall;

if (autoAssignTherapist) {
  // Case 1: Auto-assign is checked
  apiCall = postTreatmentPlanAutoAssign({
    ...payload,
    ...(form.id ? { updateUsersGuid: admin.guid } : { createUsersGuid: admin.guid }),
  });
} else if (payload.therapistsGuid && form.id && !showUnavailableTherapists) {
  // Case 2: update therapist in existing treatment plan
  apiCall = updateTherapistTakingTreatmentPlan({
    ...payload,
    updateUsersGuid: admin.guid,
  });
} else if (payload.therapistsGuid && showUnavailableTherapists) {
  // Case 4: assign to unavailable therapist
  apiCall = updateUnavailableTherapistTakingTreatmentPlan({
    ...payload,
    updateUsersGuid: admin.guid,
  });
} else {
  apiCall = postAppointmentsPrograms({
    ...payload,
    treatmentGroup: undefined,
    ...(form.id ? { updateUsersGuid: admin.guid } : { createUsersGuid: admin.guid }),
  });
}


    apiCall
      .then((res) => {
        if (res && "data" in res) {
          const responseData = res.data;
          if (responseData && typeof responseData === 'object') {
            if ('success' in responseData && (responseData as any).success === "false") {
              const errorMsg = (responseData as { message?: string }).message || null;
              setErrorMessage(errorMsg);
              setOpenModalError(true);
              return;
            }
          }

          // Update form state with the response data to ensure protocolId is preserved
          if (res.data && Array.isArray(res.data) && res.data.length > 0) {
            const updatedProgram = res.data[0];
            if ('appointmentsProgram' in updatedProgram && updatedProgram.appointmentsProgram) {
              setForm((prev) => ({
                ...prev,
                ...updatedProgram.appointmentsProgram,
                // Ensure protocolId is preserved
                protocolId: updatedProgram.appointmentsProgram.protocolId ?? prev.protocolId,
              }));
            }
          }

          if (user.Users.Guid && user.Users.Guid !== "0" && user.Users.Guid.trim() !== "") {
            const selectedIds = selectedExercises.map((v) => v.value);
            addExercises({
              type: 2,
              usersGuid: user.Users.Guid,
              body: selectedIds,
            });
          }
          else if (treatmentGroupId) {
            GetGroupTreatmentPlans({
              groupId: treatmentGroupId
            });
          }

          setOpenModalSuccess(true);
          if (user.Users.Guid && user.Users.Guid !== "0" && user.Users.Guid.trim() !== "") {
            getAppointmentsPrograms({
              companyId: admin.companyId,
              usersGuid: user.Users.Guid,
            });
          }
        }
      })
      .catch((err) => {
        // Check if error has a message in the data
        const errorMsg = err?.data?.message || err?.message || null;
        setErrorMessage(errorMsg);
        setOpenModalError(true);
        // console.log(err);
      });
  };

  const onSelectChange = useCallback(
    <
      T extends keyof IAppointmentsProgramsDataWithLabels, // Constrain to valid keys in IAppointmentsProgramsDataWithLabels
      U extends IAppointmentsProgramsDataWithLabels[T][number] // Infer the item type within the array
    >(
      value: SingleValue<U>, // Value is of type AddLabelAndValue<U>
      { name }: ActionMeta<U>
    ) => {
      if (name && value && "value" in value) {
        setForm((prev) => ({
          ...prev,
          [name]: value.value, // Update the form field with the selected value
        }));
      }
    },
    []
  );
  const onTherapistsSpecializationsChange = useCallback(
    (
      value: MultiValue<AddLabelAndValue<TNamedItem>> | null
    ) => {
      const ids = value ? value.map((item) => Number(item.value)) : [];
      setForm((prev) => ({
        ...prev,
        therapistsSpecializationsIds: ids,
      }));
    },
    []
  );
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { id, value } = e.target;
      setForm((prev) => ({ ...prev, [id]: +value }));
    },
    []
  );

  const onCompanyProgramClick = (program: Partial<IAppointmentsProgram>) => {
    setForm((prev) => ({ ...prev, ...program }));
    setShowCompaniesProgramsModal(false);
  };

  const checkAvailability = (therapistsId: number) => {
    if (
      !appointmentsProgramsTherapist ||
      !Array.isArray(appointmentsProgramsTherapist)
    ) {
      return false;
    }
    return appointmentsProgramsTherapist.some(
      (therapist) => therapist.value === therapistsId
    );
  };

  return (
    <article className={styles.section}>
      {!showExercises && !submitButtonText && <CloseButton onClick={onClose} />}
      {showExercises ? (
        <AddExerciseToUser onClose={() => setShowExercises(false)} />
      ) : (
        <form className={styles.form}>
          {treatmentGroupId && (
            <div style={{
              backgroundColor: '#e3f2fd',
              padding: '12px',
              borderRadius: '4px',
              marginBottom: '16px',
              border: '1px solid #2196f3'
            }}>
              <p style={{ margin: 0, fontWeight: 600, color: '#1976d2' }}>
                🫂 {t('GROUP_TREATMENT_PLAN')} - {user.Users.FullName}
              </p>
              <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#424242' }}>
                {t('CREATING_PLAN_FOR_ENTIRE_GROUP')}
              </p>
            </div>
          )}
          <div className={styles.form__header}>
            {isValidForChanges ? (
              <p className={styles.form__note}>{t("REQUIRED_FIELDS_NOTE")}</p>
            ) : (
              <p className={styles.form__note}>
                {t("STATUS_CHANGE_NOT_ALLOWED")}
              </p>
            )}
            {isValidForChanges && (
              <button
                type={"button"}
                className={`addBtn ${!isValidForChanges && "button_disabled"}`}
                disabled={!isValidForChanges}
                onClick={() => {
                  setShowCompaniesProgramsModal(true);
                }}
              >
                {t("SELECT_EXISTING_PROGRAM")}
                <span className={`plus`}>+</span>
              </button>
            )}
          </div>
          <ul
            className={`${styles.form__list} ${addOrEditQuestionnaireStyles.form__list
              } ${!mobile && addOrEditQuestionnaireStyles.form__list_four_columns
              }`}
          >
            <li>
              <label htmlFor="name" className={styles.form__label}>
                {t('NAME_1')}</label>
              <input
                type="text"
                className={`${formStyles.form__input} ${!isValidForChanges && formStyles.form__input_disabled
                  }`}
                name="name"
                id="name"
                disabled={!isValidForChanges}
                value={form.name || ""}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, name: e.target.value }))
                }
              />
            </li>
            <li>
              <label
                htmlFor="countOfAppointments"
                className={styles.form__label}
              >
                {t("NUMBER_OF_APPOINTMENTS")}
              </label>
              <input
                type="number"
                className={`${formStyles.form__input} ${!isValidForChanges && formStyles.form__input_disabled
                  }`}
                name="countOfAppointments"
                id="countOfAppointments"
                value={form.countOfAppointments || ""}
                onChange={handleChange}
                disabled={!isValidForChanges}
              />
            </li>
            <li>
              <label
                htmlFor="appointmentsDurationsId"
                className={styles.form__label}
              >
                {t("APPOINTMENT_DURATION")}
              </label>
              <Select
                name={"appointmentsDurationsId"}
                options={appointmentsProgramsData.AppointmentsDurations}
                value={
                  appointmentsProgramsData.AppointmentsDurations.find(
                    (o) => o.value === form?.appointmentsDurationsId
                  ) || undefined
                }
                placeholder={t("SELECT_PLACEHOLDER")}
                className={styles.select}
                onChange={onSelectChange}
                isDisabled={!isValidForChanges}
              />
            </li>
            <li>
              <label
                htmlFor="appointmentsSequenceId"
                className={styles.form__label}
              >
                {t("APPOINTMENT_FREQUENCY")}
              </label>
              <Select
                name={"appointmentsSequenceId"}
                options={appointmentsProgramsData.AppointmentsSequence}
                value={
                  appointmentsProgramsData.AppointmentsSequence.find(
                    (o) => o.value === form?.appointmentsSequenceId
                  ) || undefined
                }
                placeholder={t("SELECT_PLACEHOLDER")}
                className={styles.select}
                onChange={(value) => {
                  value &&
                    setForm((prev) => ({
                      ...prev,
                      appointmentsSequenceId: value.Id,
                    }));
                }}
                isDisabled={!isValidForChanges}
              />
            </li>
            <li>
              <label htmlFor="protocolId" className={styles.form__label}>
                {t("PROTOCOL_FOR_PATIENT")}
              </label>
              <Select
                name="protocolId"
                inputId="protocolId"
                options={protocolsData?.map(protocol => ({
                  value: protocol.id,
                  label: protocol.desc,
                })) || []}
                value={protocolsData?.find(protocol => protocol.id === form.protocolId) ? {
                  value: form.protocolId,
                  label: protocolsData.find(protocol => protocol.id === form.protocolId)?.desc || '',
                } : null}
                placeholder={t("SELECT_PROTOCOL")}
                className={styles.select}
                isClearable
                isDisabled={!isValidForChanges || (protocolsData && protocolsData.length === 0)}
                isLoading={!protocolsData}
                noOptionsMessage={() => t("NO_PROTOCOLS_AVAILABLE")}
                onChange={(value) => {
                  setForm((prev) => ({
                    ...prev,
                    protocolId: value?.value || null,
                  }));
                }}
              />
            </li>
            <li>
              <label htmlFor="roleId" className={styles.form__label}>
                {t("SUPPORT_ROLE")}
              </label>
              <Select
                name={"roleId"}
                options={appointmentsProgramsData.Roles}
                value={
                  appointmentsProgramsData.Roles.find(
                    (o) => o.value === form?.roleId
                  ) || undefined
                }
                placeholder={t("SELECT_PLACEHOLDER")}
                className={styles.select}
                isDisabled={!isValidForChanges}
                onChange={onSelectChange}
              />
            </li>
            <li>
              <label htmlFor="genderId" className={styles.form__label}>
                {t("GENDER")}
              </label>
              <Select
                name={"genderId"}
                options={appointmentsProgramsData.Genders}
                value={
                  appointmentsProgramsData.Genders.find(
                    (o) => o.value === form.genderId
                  ) || undefined
                }
                placeholder={t("SELECT_PLACEHOLDER")}
                className={styles.select}
                onChange={onSelectChange}
                isDisabled={!isValidForChanges}
              />
            </li>
            <li>
              <label htmlFor="migzarId" className={styles.form__label}>
                {t("SECTOR")}
              </label>
              <Select
                name={"migzarId"}
                options={appointmentsProgramsData.Migzars}
                value={
                  appointmentsProgramsData.Migzars.find(
                    (o) => o.value === form.migzarId
                  ) || undefined
                }
                placeholder={t("SELECT_PLACEHOLDER")}
                className={styles.select}
                onChange={onSelectChange}
                isDisabled={!isValidForChanges}
              />
            </li>
            <li>
              <label htmlFor="therapistsSpecializationsIds" className={styles.form__label}>
                {t("SPECIALIZATION")}
              </label>
              <Select
                name={"therapistsSpecializationsIds"}
                options={appointmentsProgramsData.TherapistsSpecializations}
                value={
                  form?.therapistsSpecializationsIds
                    ? appointmentsProgramsData.TherapistsSpecializations.filter(
                      (o) => form.therapistsSpecializationsIds?.includes(o.value as number)
                    )
                    : undefined
                }
                isMulti={true}
                placeholder={t("SELECT_PLACEHOLDER")}
                className={styles.select}
                onChange={(newValue) => onTherapistsSpecializationsChange(newValue as MultiValue<AddLabelAndValue<TNamedItem>> | null)}
                isDisabled={!isValidForChanges}
              />
            </li>
            <li>
              <label htmlFor="statusId" className={styles.form__label}>
                {t("COLUMN_STATUS")}
              </label>
              <Select
                name={"statusId"}
                options={appointmentsProgramsData.AppointmentsProgramsStatus}
                value={
                  appointmentsProgramsData.AppointmentsProgramsStatus.find(
                    (s) => s.Id === form.statusId
                  ) || undefined
                }
                placeholder={t("STATUS_PLACEHOLDER")}
                className={styles.select}
                onChange={onSelectChange}
                isDisabled={true}
              />
            </li>
            <li>
              <label htmlFor="mediasId" className={styles.form__label}>
                {t("MEETING_MODE")}
              </label>
              <Select
                name={"mediasId"}
                options={appointmentsProgramsData.Medias}
                value={
                  appointmentsProgramsData.Medias.find(
                    (o) => o.value === form?.mediasId
                  ) || undefined
                }
                placeholder={t("SELECT_PLACEHOLDER")}
                className={styles.select}
                onChange={onSelectChange}
                isDisabled={!isValidForChanges}
              />
            </li>
            {/* Room Assignment Section - Show when clinic is selected */}
            {isClinicSelected && (
              <li>
                <label htmlFor="treatmentRoomId" className={styles.form__label}>
                  {t("SELECT_ROOM")}
                </label>
                <Select
                  name="treatmentRoomId"
                  options={[
                    { value: -1, label: t("SET_AUTOMATICALLY") },
                    ...treatmentRooms
                  ]}
                  value={
                    form.treatmentRoomId == null
                      ? { value: -1, label: t("SET_AUTOMATICALLY") }
                      : treatmentRooms.find(room => room.value === form.treatmentRoomId)
                  }
                  placeholder={t("SELECT_ROOM_PLACEHOLDER")}
                  className={styles.select}
                  onChange={(value) => {
                    const isAutomatic = value?.value === -1;
                    const roomId: number | null = isAutomatic ? null : (typeof value?.value === 'number' ? value.value : null);
                    setForm((prev) => ({
                      ...prev,
                      treatmentRoomId: roomId,
                    }));
                  }}
                  isDisabled={!isValidForChanges}
                  formatOptionLabel={(option: any) => (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {option.label}
                    </div>
                  )}
                />
              </li>
            )}
            <li>
              <label htmlFor="vetek" className={styles.form__label}>
                {t("TENURE")}
              </label>
              <input
                type="number"
                className={`${formStyles.form__input} ${!isValidForChanges && formStyles.form__input_disabled
                  }`}
                name="vetek"
                id="vetek"
                value={form.vetek || ""}
                onChange={handleChange}
                disabled={!isValidForChanges}
              />
            </li>
            <li>
              <label htmlFor="fundingSourcesId" className={styles.form__label}>
                {t("FUNDING_SOURCE")}
              </label>
              <Select
                name={"fundingSourcesId"}
                options={appointmentsProgramsData.FundingSources}
                value={
                  appointmentsProgramsData.FundingSources.find((o) => o.value === form.fundingSourcesId) || undefined
                }
                placeholder={t("SELECT_PLACEHOLDER")}
                className={styles.select}
                onChange={onSelectChange}
                isDisabled={!isValidForChanges}
              />
            </li>
            <li>
              <label htmlFor="companyServicesId" className={styles.form__label}>
                {t("SERVICE_TYPE")}
              </label>
              <Select
                name={"companyServicesId"}
                options={appointmentsProgramsData.CompanyServicesList}
                value={
                  appointmentsProgramsData.CompanyServicesList.find(
                    (o) => o.value === form.companyServicesId
                  ) || null
                }
                placeholder={t("SELECT_PLACEHOLDER")}
                className={styles.select}
                isClearable
                onChange={onSelectChange}
                isDisabled={!isValidForChanges}
              />
            </li>
            <li>
              <label htmlFor="treatmentTypesId" className={styles.form__label}>
                {t("TREATMENT_TYPE")}
              </label>

              <Select
                name={"treatmentTypesId"}
                options={appointmentsProgramsData.TreatmentTypes}
                value={
                  appointmentsProgramsData.TreatmentTypes.find((o) => o.value === form.treatmentTypesId) || undefined
                }
                placeholder={t("SELECT_PLACEHOLDER")}
                className={styles.select}
                onChange={onSelectChange}
                isDisabled={!isValidForChanges}
              />
            </li>
            <li>
              <div className={styles.form__checkboxes}>
                <input
                  type="checkbox"
                  disabled={!isValidForChanges || !isFormValid() || autoAssignTherapist}
                  name="therapistsId"
                  id="therapistsId"
                  checked={getTherapistsOptions}
                  onChange={() => {
                    setGetTherapistsOptions((prev) => !prev);
                  }}
                />

                <label htmlFor="therapistsId" className={styles.form__label}>
                  {t("ASSIGN_THERAPIST")}
                </label>
                {showCheckboxes && (
                  <>
                    <input
                      type="checkbox"
                      disabled={!isValidForChanges || !isFormValid()}
                      name="nonAvailableTherapistsId"
                      id="nonAvailableTherapistsId"
                      checked={showUnavailableTherapists}
                      onChange={() => {
                        setShowUnavailableTherapists((prev) => !prev);
                      }}
                    />
                    <label
                      htmlFor="nonAvailableTherapistsId"
                      className={styles.form__label}
                    >
                      {t('SHOW_NOT_AVAILABLE')}</label>
                  </>
                )}
              </div>
              {program?.therapistUserName && !getTherapistsOptions && (
                <p className={`${styles.form__note}`} style={{ marginTop: '8px' }}>
                  {t("THERAPIST_NAME")}: {program.therapistUserName}
                </p>
              )}
              {getTherapistsOptions ? (
                isGettingTherapists ? (
                  <Loader />
                ) : (
                  <Select
                    name={"therapistsId"}
                    options={
                      showUnavailableTherapists
                        ? appointmentsProgramsUnavailableTherapists || []
                        : appointmentsProgramsTherapist
                    }
                    value={
                      (() => {
                        const therapistList = showUnavailableTherapists
                          ? appointmentsProgramsUnavailableTherapists
                          : appointmentsProgramsTherapist;
                        if (!therapistList) return undefined;
                        // First try to match by therapistsId
                        if (form?.therapistsId) {
                          const found = therapistList.find((o) => o.value === form.therapistsId);
                          if (found) return found;
                        }
                        // If therapistsId not found, try to match by therapistsGuid
                        if (form?.therapistsGuid) {
                          const found = therapistList.find((o) => o.therapistsGuid === form.therapistsGuid);
                          if (found) return found;
                        }
                        return undefined;
                      })()
                    }
                    placeholder={t("SELECT_PLACEHOLDER")}
                    className={styles.select}
                    onChange={(value) => {
                      value &&
                        setForm((prev) => ({
                          ...prev,
                          therapistsId: value.value,
                          therapistsGuid: value.therapistsGuid,
                          available: checkAvailability(value.therapistsId),
                        }));
                    }}
                    isDisabled={!isValidForChanges}
                  />
                )
              ) : !isFormValid() && (
                <p className={`${styles.form__note}`}>
                  {t("FILL_REQUIRED_FIELDS")}
                </p>
              )}
            </li>
            <li>
              <div className={styles.form__checkboxes}>
                <input
                  type="checkbox"
                  disabled={!isValidForChanges || !!form?.therapistsGuid}
                  name="autoAssignTherapist"
                  id="autoAssignTherapist"
                  checked={autoAssignTherapist}
                  onChange={() => {
                    setAutoAssignTherapist((prev) => !prev);
                  }}
                />
                <label htmlFor="autoAssignTherapist" className={styles.form__label}>
                  {t("AUTOMATIC_ALLOCATION")}
                </label>
              </div>
              {!isFormValid() && (
                <p className={`${styles.form__note}`}>
                  {t("FILL_REQUIRED_FIELDS")}
                </p>
              )}
              {!autoAssignTherapist && isFormValid() && (
                <p className={`${styles.form__note}`}>
                  *{t("AUTOMATIC_ALLOCATION_NOTE")}
                </p>
              )}
            </li>
            <li>
              <label htmlFor="exercises" className={styles.form__label}>
                {t("ADD_EXERCISE")}
              </label>
              <Select
                name={"exercises"}
                options={exercises}
                value={selectedExercises || []}
                placeholder={t("SELECT_PLACEHOLDER")}
                className={styles.select}
                isMulti={true}
                onChange={(values) => {
                  setSelectedExercises(values || []);
                }}
                isDisabled={!isValidForChanges}
              />
            </li>
            <li>
              <label htmlFor="comments" className={styles.form__label}>
                {t("COMMENTS")}
              </label>
              <textarea
                ref={textAreaRef}
                rows={1}
                className={`${formStyles.form__input} ${!isValidForChanges && formStyles.form__input_disabled
                  }`}
                name="comments"
                id="comments"
                value={form.comments || ""}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, comments: e.target.value }))
                }
                disabled={!isValidForChanges}
              />
            </li>
          </ul>
          {createDate && (
            <p className={styles.form__note}>
              {t('CREATION_DATE_1')}{getPrintDate(new Date(createDate))}
            </p>
          )}
          <button
            type={"button"}
            className={`button ${styles.submitBtn} ${(!isValidForChanges || !isFormValid() || isSending || isAutoAssigning) &&
              `button_disabled`
              }`}
            onClick={sendForm}
            disabled={isSending || isAutoAssigning || !isValidForChanges || !isFormValid()}
          >
            {(isSending || isAutoAssigning) ? t("SUBMITTING_LABEL") : (submitButtonText ? t(submitButtonText) : t("KEEP_CHANGES"))}
          </button>
        </form>
      )}
      {openModalSuccess && (
        <Modal onClose={() => setOpenModalSuccess(false)}>
          <GeneralSuccessPopup
            onClose={() => {
              setOpenModalSuccess(false);
              onClose();
            }}
          />
        </Modal>
      )}
      {openModalError && (
        <Modal onClose={() => {
          setOpenModalError(false);
          setErrorMessage(null);
        }}>
          <ErrorPopup
            text={errorMessage || undefined}
            onClose={() => {
              setOpenModalError(false);
              setErrorMessage(null);
            }}
          />
        </Modal>
      )}
      {showCompaniesProgramsModal && (
        <Modal onClose={() => setShowCompaniesProgramsModal(false)} wide={true}>
          <CompanyProgramsPopup
            companyId={admin.companyId}
            onClick={onCompanyProgramClick}
          />
        </Modal>
      )}
    </article>
  );
}

export default AddTreatmentPlan;

